import { ExternalLink } from 'lucide-react'
import { ChannelMark } from '@/components/channel-icons'
import { cn } from '@/lib/utils'

interface ReservationExternalLinksProps {
  reservationId?: string | null
  /** The stay's check-in day, which Uplisting's booking page opens on. */
  checkIn?: string | null
  channel?: string | null
  /** The channel's own booking id; the Airbnb link cannot be built without it. */
  externalReservationId?: string | null
  className?: string
}

const LINK_CLASSES =
  'inline-flex items-center gap-1.5 text-sm text-text transition-colors hover:text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 rounded-sm'

/**
 * Where else this booking lives: its Uplisting page, and its Airbnb page for
 * Airbnb bookings.
 *
 * Extracted from the reservation page so the calendar's stay sheet shows the
 * same links, built the same way, rather than a second copy that would drift
 * when a link's URL changes. Renders nothing when there is no link to offer.
 * (There is no Vrbo equivalent: nothing here knows a Vrbo booking URL, so a
 * Vrbo booking is reached through its Uplisting page.)
 */
export function ReservationExternalLinks({
  reservationId,
  checkIn,
  channel,
  externalReservationId,
  className,
}: ReservationExternalLinksProps) {
  const showUplisting = Boolean(reservationId && checkIn)
  const showAirbnb =
    (channel === 'airbnb' || channel === 'airbnb_official') && Boolean(externalReservationId)
  if (!showUplisting && !showAirbnb) return null

  return (
    <div className={cn('border-t border-border pt-4', className)}>
      <div className="mb-2 text-xs text-textMuted">External links</div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {showUplisting && (
          <a
            href={`https://app.uplisting.io/calendar/bookings/${reservationId}/summary?from=${checkIn}`}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASSES}
          >
            <span>Uplisting</span>
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
        {showAirbnb && (
          <a
            href={`https://www.airbnb.com/hosting/reservations/details/${externalReservationId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASSES}
          >
            <ChannelMark channel={channel ?? ''} className="h-3.5 w-3.5" />
            <span>Airbnb</span>
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  )
}
